/**
 * HourlySlotPicker — guest-facing slot selector on the hotel detail page.
 *
 * Fetches /api/guest/rooms/{room_id}/slot-availability?date=YYYY-MM-DD
 * and lets the user toggle one or more enabled+available slots.
 *
 * Reports back via `onChange({ slots: [...], total: <sum of prices> })`.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Check, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HourlySlotPicker = ({ roomId, date, selectedSlots, onChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!roomId || !date) { setData(null); return; }
    setLoading(true); setError('');
    try {
      const { data: d } = await axios.get(`${API}/guest/rooms/${roomId}/slot-availability`, { params: { date } });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load slots');
      setData(null);
    } finally { setLoading(false); }
  }, [roomId, date]);

  useEffect(() => { load(); }, [load]);

  // Recompute total whenever selections or available slot prices change.
  const slots = data?.slots || [];
  const priceMap = Object.fromEntries(slots.map((s) => [s.slot_id, s.price]));

  const toggle = (slotId) => {
    const cur = new Set(selectedSlots || []);
    if (cur.has(slotId)) cur.delete(slotId);
    else cur.add(slotId);
    const next = Array.from(cur);
    const total = next.reduce((sum, id) => sum + (priceMap[id] || 0), 0);
    onChange({ slots: next, total });
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 p-4 bg-[#FAFAFA] text-xs text-gray-500" data-testid="hourly-slots-loading">
        Loading slots…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-xl border border-gray-200 p-4 bg-[#FAFAFA] text-xs text-gray-500" data-testid="hourly-slots-empty">
        Pick a date to see hourly slots.
      </div>
    );
  }
  if (!data.hourly_pricing_enabled) {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="hourly-slots-disabled">
        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
        <p className="text-xs text-amber-800 font-bold">Hourly booking isn't enabled for this room.</p>
      </div>
    );
  }
  if (data.is_closed) {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3" data-testid="hourly-slots-closed">
        <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
        <p className="text-xs text-red-800 font-bold">Rooms are closed for this date.</p>
      </div>
    );
  }
  if (error) {
    return <p className="text-xs text-red-600">{error}</p>;
  }

  const visibleSlots = slots.filter((s) => s.enabled);
  if (visibleSlots.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 p-3 bg-[#FAFAFA]" data-testid="hourly-slots-none">
        <p className="text-xs text-gray-600">No hourly slots configured for this room yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="hourly-slots-grid">
      {visibleSlots.map((s) => {
        const selected = (selectedSlots || []).includes(s.slot_id);
        const soldOut = s.available <= 0;
        return (
          <button
            key={s.slot_id}
            type="button"
            disabled={soldOut}
            onClick={() => toggle(s.slot_id)}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border-2 transition-colors ${
              soldOut
                ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                : selected
                ? 'bg-[#FFE5EE] border-[#FF0059]'
                : 'bg-white border-gray-200 hover:border-[#FF0059]'
            }`}
            data-testid={`hourly-slot-${s.slot_id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {selected && <Check className="h-4 w-4 text-[#FF0059] flex-shrink-0" data-testid={`hourly-slot-check-${s.slot_id}`} />}
              <div className="text-left min-w-0">
                <p className="text-sm font-black text-black leading-tight">{s.label}</p>
                <p className="text-[10px] text-gray-500 leading-tight">
                  {soldOut ? 'Sold out' : `${s.available} room${s.available === 1 ? '' : 's'} left`}
                </p>
              </div>
            </div>
            <span className="text-sm font-black text-[#FF0059] flex-shrink-0">₹{s.price.toLocaleString('en-IN')}</span>
          </button>
        );
      })}
    </div>
  );
};
